// <marquee-track>: seamless CSS marquee. Duplicates its [data-marquee-track] child
// until the track is at least twice the container width, then activates a -50%
// translate loop. Optional data-desktop / data-mobile attributes ("false" disables)
// gate it per device; an absent attribute means on.
//
// data-drag="true" swaps the transform animation for a native horizontal scroller
// that JS advances instead, because a transform animation cannot be dragged. In that
// mode the strip yields to the visitor -- mouse drag, touch swipe, trackpad -- and
// picks the loop back up RESUME_MS after they stop.
//
// Pace comes from --marquee-speed, in PIXELS PER SECOND, and this element derives
// --marquee-duration from it once the track is built. The setting used to be the
// duration itself, which quietly made the pixel pace a function of how much content the
// merchant had: cutting the brand strip from 21 tiles to 8 shortened the track from
// ~5124px to ~1952px and so slowed the strip from ~146px/s to ~56px/s, which reads as
// frozen. A speed is invariant to the content; the duration is not.

// Long enough that the gap between two drags is not read as the strip taking over
// again, short enough that it does not look broken after one flick.
const RESUME_MS = 1000;

// A press never lands perfectly still. Below this the gesture was a click on a tile,
// above it the visitor was dragging the strip and did not mean to navigate.
const DRAG_SLOP = 5;

customElements.define(
  "marquee-track",
  class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector("[data-marquee-track]");
      if (!this.track) return;
      this.base = this.track.innerHTML;
      // Captured before any cloning: every child past this index is a copy.
      this.baseCount = this.track.children.length;
      this.draggable_ = this.dataset.drag === "true";
      this.mq = window.matchMedia("(min-width: 768px)");
      this.reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.active = null;
      this.looping = null;
      this.listeners?.abort();
      this.listeners = new AbortController();
      this.sync();
      this.mq.addEventListener("change", () => this.sync(), { signal: this.listeners.signal });
      this.reduce.addEventListener("change", () => this.sync(), { signal: this.listeners.signal });
      if (this.draggable_) this.bindControls();
    }
    // Per-device gating must happen here rather than in CSS: an `animation: none`
    // media query would leave the clones in the DOM as visible duplicate content.
    // Only JS can un-clone.
    sync() {
      const on = this.dataset[this.mq.matches ? "desktop" : "mobile"] !== "false";
      // Reduced motion keeps the strip -- and in drag mode keeps hand control of it --
      // but drops the loop. With nothing looping, the copies are dead weight: no
      // seam for them to hide, and a second helping of the same content in the DOM.
      const loop = on && !this.reduce.matches;
      if (on === this.active && loop === this.looping) return;
      this.active = on;
      this.looping = loop;
      this.track.innerHTML = this.base;
      // Cleared, not overwritten: clone() recomputes it from the track it builds, and a
      // duration left over from a longer track would set the pace for a strip that no
      // longer exists.
      this.track.style.removeProperty("--marquee-duration");
      this.classList.toggle("is-drag", on && this.draggable_);
      this.stop();
      if (loop) this.clone();
      // After clone(), so the animation begins with the duration the cloned track
      // actually needs rather than starting on the CSS fallback and changing pace.
      this.track.classList.toggle("is-ready", on && !this.draggable_);
      if (loop && this.draggable_) this.start();
    }
    disconnectedCallback() {
      this.stop();
      clearTimeout(this.resumeId);
      this.listeners?.abort();
    }
    // The old version cloned exactly once, which assumed the track already exceeded
    // the container -- true for seven wide logo tiles, false for four short
    // announcements, where -50% then exposed a visible gap. The final full doubling
    // is what keeps the halves equal, so the copy count must stay even.
    clone() {
      let guard = 0;
      while (this.track.scrollWidth < this.clientWidth && guard++ < 12) {
        this.track.insertAdjacentHTML("beforeend", this.base);
      }
      this.track.insertAdjacentHTML("beforeend", this.track.innerHTML);
      this.hideClones();
      // The CSS mode animates translateX(-50%) over --marquee-duration, so the duration
      // that yields the merchant's pixel speed can only be known once the track exists.
      // Writing it here is what lets ONE speed setting drive both modes.
      this.track.style.setProperty("--marquee-duration", this.track.scrollWidth / 2 / this.speed() + "s");
    }
    // The copies are duplicate content: read out, a screen reader announces the whole
    // strip two or more times, and now that tiles can be links they would also double
    // every tab stop.
    hideClones() {
      const focusable = "a, button, input, select, textarea, [tabindex]";
      const kids = this.track.children;
      for (let i = this.baseCount; i < kids.length; i++) {
        kids[i].setAttribute("aria-hidden", "true");
        if (kids[i].matches(focusable)) kids[i].setAttribute("tabindex", "-1");
        kids[i].querySelectorAll(focusable).forEach((el) => el.setAttribute("tabindex", "-1"));
      }
    }
    // Drag mode's auto-advance, at the same --marquee-speed the CSS mode's duration is
    // derived from, so one merchant setting drives both modes.
    start() {
      // hovered is checked here rather than at the call site so nothing -- a drag ending
      // under the cursor, a wheel tick, a blur -- can hand the loop back while someone is
      // still reading a tile.
      if (this.rafId || !this.looping || this.hovered) return;
      let last = null;
      // The position is accumulated HERE, in a float, rather than read back out of
      // scrollLeft each frame. scrollLeft rounds to whole pixels (assign 10.5, read 11),
      // so a read-modify-write threw away the sub-pixel remainder every frame and the
      // strip advanced in whole pixels per frame instead: at 60Hz, 40px/s and 80px/s
      // both came out at ~60px/s, and anything under half a pixel a frame would not have
      // moved at all. Seeded from scrollLeft on every start, which is what picks up
      // wherever a drag, swipe or wheel left the strip.
      this.pos = this.scrollLeft;
      const step = (now) => {
        if (last !== null) {
          const perMs = this.speed() / 1000;
          this.pos = this.wrap(this.pos + perMs * (now - last), this.track.scrollWidth / 2);
          this.scrollLeft = this.pos;
        }
        last = now;
        this.rafId = requestAnimationFrame(step);
      };
      this.rafId = requestAnimationFrame(step);
    }
    stop() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
    // The second copy sits exactly where the first began, so folding by half a track
    // is invisible. Folding forwards is the loop; folding backwards is a reverse drag
    // running off the front, where the browser would otherwise clamp at 0.
    wrap(x, half) {
      if (!(half > 0)) return x;
      const m = x % half;
      return m < 0 ? m + half : m;
    }
    // Pixels per second, from --marquee-speed. The fallback is a sane pace rather than a
    // guess at a duration: with no speed declared there is nothing content-independent
    // to derive one from.
    speed() {
      const raw = parseFloat(getComputedStyle(this.track).getPropertyValue("--marquee-speed"));
      return raw > 0 ? raw : 120;
    }
    // Mouse gets a hand-written drag because a mouse press does not scroll anything on
    // its own. Touch and trackpad already scroll the host natively -- with momentum we
    // could not reproduce -- so there we only stand down and wait.
    bindControls() {
      const on = (target, type, fn, opts) =>
        target.addEventListener(type, fn, { signal: this.listeners.signal, ...opts });

      on(this, "pointerdown", (e) => {
        if (e.pointerType !== "mouse" || e.button !== 0) return;
        // Stops the browser turning a drag across a logo into a native image drag.
        e.preventDefault();
        this.hold();
        this.swallowClick = false;
        this.drag = { x: e.clientX, from: this.scrollLeft, moved: 0 };
        this.classList.add("is-grabbing");
      });
      on(window, "pointermove", (e) => {
        if (!this.drag) return;
        const dx = e.clientX - this.drag.x;
        this.drag.moved = Math.max(this.drag.moved, Math.abs(dx));
        const half = this.looping ? this.track.scrollWidth / 2 : 0;
        this.scrollLeft = this.wrap(this.drag.from - dx, half);
      });
      const release = () => {
        if (!this.drag) return;
        this.swallowClick = this.drag.moved > DRAG_SLOP;
        this.drag = null;
        this.classList.remove("is-grabbing");
        this.resumeSoon();
      };
      on(window, "pointerup", release);
      on(window, "pointercancel", release);

      // Capture, so the tile's own navigation is cancelled before it happens: without
      // this, every drag across the strip would also open whichever logo it started on.
      on(this, "click", (e) => {
        if (this.swallowClick) e.preventDefault();
        this.swallowClick = false;
      }, { capture: true });

      on(this, "pointerenter", () => { this.hovered = true; this.hold(); });
      on(this, "pointerleave", () => { this.hovered = false; this.resumeSoon(); });

      on(this, "touchstart", () => this.hold(), { passive: true });
      on(this, "touchend", () => this.resumeSoon(), { passive: true });
      on(this, "touchcancel", () => this.resumeSoon(), { passive: true });
      // Every wheel tick re-arms the same timer, so a long trackpad sweep counts as one
      // interaction rather than a fight between finger and loop.
      on(this, "wheel", () => { this.hold(); this.resumeSoon(); }, { passive: true });
      // Tabbing onto a tile scrolls it into view, which the loop would immediately undo.
      on(this, "focusin", () => this.hold());
      on(this, "focusout", () => this.resumeSoon());
    }
    hold() {
      clearTimeout(this.resumeId);
      this.stop();
    }
    resumeSoon() {
      clearTimeout(this.resumeId);
      this.resumeId = setTimeout(() => this.start(), RESUME_MS);
    }
  }
);
